import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { UIMessage } from 'ai';
import { ArrowLeftIcon, SendIcon } from 'lucide-react';
import {
  Button,
  ErrorNotice,
  LoadingState,
  PageHeading,
  Panel,
  cn,
} from '@bcecloud/portal-ui';
import { MessageResponse } from '@/components/ai-elements/message';
import { assistantMarkdownComponents } from '@/components/assistant/rich';
import { ToolChip, type ToolPart } from '@/components/assistant/AssistantPanel';
import {
  requestAssistantOpen,
  saveMessages,
  setCurrentChatId,
} from '@/components/assistant/assistant-store';
import { errorMessage, getJson } from '@/lib/api';

/**
 * One conversation, read-only, at its own URL. "Continue this chat" loads it
 * into the assistant panel via the store's open-request listener.
 */

interface ChatTranscript {
  readonly id: string;
  readonly title: string;
  readonly messages: UIMessage[];
}

export function ChatViewPage(): React.ReactElement {
  const navigate = useNavigate();
  const chatId = useParams<{ chatId: string }>().chatId ?? '';

  const transcript = useQuery({
    queryKey: ['assistant-chat', chatId],
    queryFn: ({ signal }) => getJson<ChatTranscript>(`/assistant/chats/${chatId}`, undefined, signal),
  });

  /** Loads the conversation into the panel and asks the shell to open it. */
  function continueChat(chat: ChatTranscript): void {
    saveMessages(chat.messages);
    setCurrentChatId(chat.id);
    requestAssistantOpen();
  }

  if (transcript.isPending) return <LoadingState label="Loading conversation…" />;
  if (transcript.isError) {
    return <ErrorNotice title="Could not load this chat" message={errorMessage(transcript.error)} />;
  }
  const chat = transcript.data;

  return (
    <section className="animate-portal-rise">
      <div className="flex items-start justify-between gap-4">
        <PageHeading
          eyebrow="AI ASSISTANT"
          title={chat.title}
          subtitle="Read-only transcript — continue it in the panel to keep talking"
        />
        <span className="flex shrink-0 items-center gap-2">
          <Button variant="secondary" onClick={() => navigate('/assistant/chats')}>
            <ArrowLeftIcon aria-hidden className="size-4" /> All chats
          </Button>
          <Button onClick={() => continueChat(chat)}>
            <SendIcon aria-hidden className="size-4" /> Continue this chat
          </Button>
        </span>
      </div>
      <Panel className="p-5">
        <div className="space-y-4">
          {chat.messages.map((message) => (
            <div
              key={message.id}
              className={cn('flex', message.role === 'user' ? 'justify-end' : 'justify-start')}
            >
              <div
                className={cn(
                  'max-w-[85%] text-portal-small',
                  message.role === 'user' ? 'rounded-lg bg-portal-bg px-3 py-2' : 'text-portal-ink',
                )}
              >
                {message.parts.map((part, index) =>
                  part.type === 'text' ? (
                    message.role === 'assistant' ? (
                      <MessageResponse
                        key={`${message.id}-${index}`}
                        components={assistantMarkdownComponents}
                      >
                        {part.text}
                      </MessageResponse>
                    ) : (
                      <span key={`${message.id}-${index}`} className="whitespace-pre-wrap">
                        {part.text}
                      </span>
                    )
                  ) : part.type.startsWith('tool-') || part.type === 'dynamic-tool' ? (
                    <ToolChip key={`${message.id}-${index}`} part={part as ToolPart} />
                  ) : null,
                )}
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </section>
  );
}
